import { useState, useRef, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const formRef = useInView();
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = "Contact Resecor | Start a Conversation About UK Defence & Security Markets";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Get in touch with Resecor. Tell us what you are working on and we will tell you whether we can help. Enquiries about market entry, pursuit architecture, and defence procurement advisory.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${import.meta.env.VITE_SITE_URL ?? ""}/contact`);

    const scriptId = "jsonld-contact";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/contact#webpage`,
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/contact`,
          "name": "Contact Resecor | Start a Conversation About UK Defence & Security Markets",
          "description": "Get in touch with Resecor. Tell us what you are working on and we will tell you whether we can help.",
          "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#website` }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What types of enquiries does Resecor receive?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Resecor receives enquiries about market entry assessment, pursuit architecture, investor contractability review, and buyer-side procurement support."
              }
            },
            {
              "@type": "Question",
              "name": "How quickly does Resecor respond to enquiries?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Resecor typically responds within one working day."
              }
            },
            {
              "@type": "Question",
              "name": "How can I contact Resecor?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can contact Resecor by email at enquiries@resecor.com or by submitting the contact form on this page."
              }
            }
          ]
        }
      ]
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus("submitting");

    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d75dajucsbv12mkudkgg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-navy font-body">
      <Navbar />

      {/* ── PAGE HERO ── */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/139844c210557ac2414245e4e6d7e13c.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <span className="font-heading text-xs tracking-[0.25em] uppercase text-copper font-medium">Contact</span>
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-white leading-tight max-w-lg">
            Start a conversation.
          </h1>
          <p className="mt-5 font-body text-base text-mid-grey max-w-lg leading-relaxed">
            Tell us what you are working on. We will tell you whether we can help.
          </p>
          <div className="mt-6 w-12 h-0.5 bg-copper" />
        </div>
      </section>

      {/* ── FORM + DETAILS ── */}
      <section className="bg-navy pb-24 md:pb-32" ref={formRef.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

            {/* Form */}
            <div
              className={`lg:col-span-7 transition-all duration-700 ${formRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              {formStatus === "success" ? (
                <div className="flex flex-col items-start gap-5 py-10">
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-copper/15">
                    <i className="ri-check-line text-copper text-2xl" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-white">Message received.</h2>
                  <p className="font-body text-base text-mid-grey leading-relaxed max-w-sm">
                    Thank you for getting in touch. We typically respond within one working day.
                  </p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-2 px-6 py-3 border border-white/20 text-white hover:bg-white/5 font-body text-sm font-medium cursor-pointer whitespace-nowrap rounded-sm transition-all duration-200"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="flex flex-col gap-2">
                      <label htmlFor="name" className="font-body text-xs text-mid-grey tracking-wide uppercase font-medium">
                        Name <span className="text-copper">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="bg-slate border border-white/8 text-white placeholder-mid-grey/40 font-body text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-copper/50 transition-colors duration-200"
                      />
                    </div>
                    {/* Organisation */}
                    <div className="flex flex-col gap-2">
                      <label htmlFor="organisation" className="font-body text-xs text-mid-grey tracking-wide uppercase font-medium">
                        Organisation <span className="text-copper">*</span>
                      </label>
                      <input
                        id="organisation"
                        name="organisation"
                        type="text"
                        required
                        placeholder="Company or organisation"
                        className="bg-slate border border-white/8 text-white placeholder-mid-grey/40 font-body text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-copper/50 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="font-body text-xs text-mid-grey tracking-wide uppercase font-medium">
                      Email <span className="text-copper">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="bg-slate border border-white/8 text-white placeholder-mid-grey/40 font-body text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-copper/50 transition-colors duration-200"
                    />
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="message" className="font-body text-xs text-mid-grey tracking-wide uppercase font-medium">
                        Message <span className="text-copper">*</span>
                      </label>
                      <span className={`font-body text-xs ${charCount > 500 ? "text-red-400" : "text-mid-grey/50"}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="Tell us what you are working on..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="bg-slate border border-white/8 text-white placeholder-mid-grey/40 font-body text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-copper/50 transition-colors duration-200 resize-none"
                    />
                    {charCount > 500 && (
                      <span className="font-body text-xs text-red-400">Message must not exceed 500 characters.</span>
                    )}
                  </div>

                  {formStatus === "error" && (
                    <p className="font-body text-sm text-red-400">Something went wrong. Please try again or email us directly.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "submitting" || charCount > 500}
                    className="self-start px-8 py-3.5 bg-copper text-white hover:bg-copper-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-body font-medium text-sm tracking-wide cursor-pointer whitespace-nowrap rounded-sm flex items-center gap-2"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Sending&hellip;
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-arrow-right-line text-base" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact details sidebar */}
            <div
              className={`lg:col-span-5 flex flex-col gap-8 transition-all duration-700 delay-200 ${formRef.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <div className="flex flex-col gap-5 p-8 bg-slate rounded-sm border border-white/5">
                <div className="flex flex-col gap-2">
                  <span className="font-heading text-xs tracking-[0.2em] uppercase text-mid-grey/60 font-medium">Email</span>
                  <a
                    href="mailto:enquiries@resecor.com"
                    className="font-body text-sm text-copper hover:text-copper-light transition-colors duration-200 cursor-pointer"
                    rel="nofollow"
                  >
                    enquiries@resecor.com
                  </a>
                </div>
                <div className="w-full h-px bg-white/5" />
                <div className="flex flex-col gap-2">
                  <span className="font-heading text-xs tracking-[0.2em] uppercase text-mid-grey/60 font-medium">Response time</span>
                  <span className="font-body text-sm text-mid-grey">
                    We typically respond within one working day.
                  </span>
                </div>
                <div className="w-full h-px bg-white/5" />
                <div className="flex flex-col gap-3">
                  <span className="font-heading text-xs tracking-[0.2em] uppercase text-mid-grey/60 font-medium">Enquiries we receive</span>
                  {[
                    "Market entry assessment",
                    "Pursuit architecture",
                    "Investor contractability review",
                    "Buyer-side procurement support",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-1 h-1 rounded-full bg-copper flex-shrink-0" />
                      <span className="font-body text-sm text-mid-grey">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
